/**
 * Deep clone utility functions
 * Provides consistent deep cloning across the codebase
 */

/**
 * Deep clones an object using JSON serialization
 * Note: This will not work with functions, undefined values, circular references,
 * or special objects like Date, RegExp, Map, Set
 *
 * @param obj - The object to clone
 * @returns A deep clone of the object
 * @throws Error if the object cannot be serialized
 */
export function deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
}

/**
 * Deep clones an object with error handling and optional fallback
 * Returns the fallback value if cloning fails
 *
 * @param obj - The object to clone
 * @param fallback - Optional fallback value if cloning fails (defaults to null)
 * @returns A deep clone of the object, or the fallback value
 */
export function deepCloneWithFallback<T>(obj: T, fallback: T | null = null): T | null {
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch {
        return fallback;
    }
}

/**
 * Deep clones an object, returning undefined if cloning fails
 * Useful for optional cloning scenarios
 *
 * @param obj - The object to clone
 * @returns A deep clone of the object, or undefined if cloning fails
 */
export function safeDeepClone<T>(obj: T): T | undefined {
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch {
        return undefined;
    }
}
